/*
 * <scentt-finder> \u2013 fragrance finder quiz.
 * Flow: intro \u2192 one question per block \u2192 results. Every answer carries product tags (data-keyword).
 * Matching: the tag index (?section_id=scentt-finder-index on the all-products collection, 250 per page)
 * is ranked in the browser \u2013 "must match" questions filter, the other answers rank. Product cards come from
 * the shared card endpoint (/products/<handle>?section_id=scentt-card). If the index can't be loaded the quiz
 * falls back to Shopify search (?section_id=scentt-finder-results), relaxing the later answers one by one.
 * If the network fails completely a friendly message is shown and the quiz stays usable.
 * Shopper-visible text is only ever set with textContent; HTML is only imported from our own sections.
 */
(() => {
  if (customElements.get('scentt-finder')) return;
  const S = window.Scentt && Object.isFrozen(window.Scentt) && typeof window.Scentt.html === 'function' ? window.Scentt : null;
  if (!S) return;

  /* ---------- helpers ---------- */
  const norm = (v) => String(v == null ? '' : v).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80);
  const ALIAS = {
    him: 'for him', her: 'for her', men: 'for him', women: 'for her', mens: 'for him', womens: 'for her',
    male: 'for him', female: 'for her', 'for men': 'for him', 'for women': 'for her',
  };
  const wanted = (keyword) => String(keyword || '').slice(0, 200).split(',').map(norm).filter(Boolean).slice(0, 10).map((k) => ALIAS[k] || k);
  /** Only same-origin relative paths ("/collections/x"), never "//host", "javascript:" or anything with spaces/quotes. */
  const safePath = (v, def = '') => {
    const s = String(v || '').trim();
    return s.length < 500 && /^\/(?!\/)[^\s"'<>\\]*$/.test(s) ? s : def;
  };
  const preview = (sep) => {
    const t = window.Shopify && window.Shopify.theme;
    return t && t.role && t.role !== 'main' && S.isId(t.id) ? `${sep}preview_theme_id=${t.id}` : '';
  };
  const SKIP_TAGS = ['sample', 'free sample', 'gift wrap', 'hidden'];
  const SET_TAGS = ['gift set', 'discovery set'];

  /* ---------- tag index (shared by every finder on the page, retried after a failure) ---------- */
  const indexes = new Map();
  const loadIndex = (allPath) => {
    if (indexes.has(allPath)) return indexes.get(allPath);
    const sep = allPath.includes('?') ? '&' : '?';
    const page = (n) => S.fetchSection(`${allPath}${sep}page=${n}${preview('&')}`, 'scentt-finder-index').then((doc) => {
      const list = doc.querySelector('[data-finder-index]');
      if (!list) throw new S.Error('no index');
      const items = Array.from(list.querySelectorAll('li[data-h]')).slice(0, 250).map((li) => ({
        h: li.getAttribute('data-h') || '',
        v: norm(li.getAttribute('data-v')),
        t: String(li.getAttribute('data-t') || '').split('|').slice(0, 250).map(norm).filter(Boolean),
        a: li.getAttribute('data-a') === 'true',
      })).filter((x) => S.isHandle(x.h));
      return { items, pages: S.int(list.getAttribute('data-pages'), 1, 1, 8) };
    });
    const ready = page(1).then(async (first) => {
      const rest = [];
      for (let n = 2; n <= first.pages; n += 1) rest.push(page(n));
      const more = await Promise.allSettled(rest);
      return more.reduce((all, r) => (r.status === 'fulfilled' ? all.concat(r.value.items) : all), first.items);
    });
    ready.catch(() => indexes.delete(allPath));
    indexes.set(allPath, ready);
    return ready;
  };

  /* ---------- product cards from the shared card endpoint (cached per page) ---------- */
  const cards = new Map();
  const fetchCard = (handle) => {
    if (!S.isHandle(handle)) return Promise.resolve(null);
    if (!cards.has(handle)) {
      const p = S.fetchSection(`/products/${encodeURIComponent(handle)}${preview('?')}`, 'scentt-card')
        .then((doc) => doc.querySelector('.scentt-card-item'));
      p.catch(() => cards.delete(handle));
      cards.set(handle, p);
    }
    return cards.get(handle).catch(() => null);
  };

  /* ---------- ranking ---------- */
  const rank = (items, answers) => {
    const req = [];
    const opt = [];
    answers.forEach((a) => { const w = wanted(a.keyword); if (w.length) (a.required ? req : opt).push(w); });
    const asksSets = req.concat(opt).some((w) => SET_TAGS.some((t) => w.includes(t)));
    const list = [];
    items.forEach((p) => {
      const tags = p.t;
      if (SKIP_TAGS.some((t) => tags.includes(t))) return;
      const has = (w) => w.some((k) => tags.includes(k));
      if (!req.every(has)) return;
      let matched = 0;
      let score = 0;
      opt.forEach((w, j) => { if (has(w)) { matched += 1; score += 100 + (opt.length - j); } });
      if (p.a) score += 5;
      /* "For him" / "For her": dedicated scents first, then unisex ones */
      req.forEach((w) => { if ((w.includes('for him') || w.includes('for her')) && !tags.includes('unisex')) score += 2; });
      if (tags.includes('coming soon')) score -= 20;
      if (!asksSets && SET_TAGS.some((t) => tags.includes(t))) score -= 3;
      list.push({ h: p.h, v: p.v, score, matched, r: Math.random() });
    });
    list.sort((x, y) => y.score - x.score || x.r - y.r);
    return { list, req, opt };
  };

  /** Pick `count` products, mixing brands among equally good matches. */
  const pickTop = (list, count) => {
    const top = [];
    const used = new Set();
    const pool = list.slice();
    while (top.length < count && pool.length) {
      const best = pool[0].score;
      let pick = 0;
      for (let k = 0; k < pool.length && pool[k].score === best; k += 1) { if (!used.has(pool[k].v)) { pick = k; break; } }
      const m = pool.splice(pick, 1)[0];
      used.add(m.v);
      top.push(m);
    }
    return top;
  };

  class ScenttFinder extends HTMLElement {
    connectedCallback() {
      this.steps = Array.from(this.querySelectorAll('.scentt-finder__step'));
      this.questions = this.steps.filter((s) => s.dataset.step === 'question');
      this.intro = this.steps.find((s) => s.dataset.step === 'intro');
      this.result = this.steps.find((s) => s.dataset.step === 'result');
      if (!this.result || !this.questions.length) return;
      this.box = this.result.querySelector('[data-finder-results]');
      this.link = this.result.querySelector('[data-finder-link]');
      this.chips = this.result.querySelector('[data-finder-chips]');
      this.allPath = safePath(this.dataset.all, '/collections/all');
      this.searchPath = safePath(this.dataset.search, '/search');
      this.count = S.int(this.dataset.count, 4, 1, 8);
      this.answers = [];
      this.current = 0;
      this.run = 0;
      if (this.link) {
        this.linkLabel = this.link.textContent.trim();
        this.defaultHref = safePath(this.link.getAttribute('href'), this.allPath);
      }

      this.onClick = (e) => this.handleClick(e);
      this.addEventListener('click', this.onClick);
      this.onBlockSelect = (e) => {
        const step = this.questions.find((q) => q.dataset.blockId === (e.detail && e.detail.blockId));
        if (step) this.goQuestion(this.questions.indexOf(step), false);
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }

    disconnectedCallback() {
      this.run += 1;
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    handleClick(e) {
      const t = e.target;
      const more = t.closest('[data-finder-expand]');
      if (more) { e.preventDefault(); if (more.getAttribute('aria-disabled') !== 'true') this.showMore(more); return; }
      if (t.closest('[data-finder-start]')) {
        this.answers = [];
        this.goQuestion(0, true);
        if (this.dataset.showProducts === 'true') loadIndex(this.allPath).catch(() => {}); /* warm up */
        return;
      }
      const ans = t.closest('.scentt-finder__answer');
      if (ans && this.contains(ans)) {
        const step = ans.closest('.scentt-finder__step');
        this.answers[this.current] = {
          label: String(ans.getAttribute('data-label') || ans.textContent || '').slice(0, 80),
          keyword: String(ans.getAttribute('data-keyword') || '').trim().slice(0, 200),
          link: safePath(ans.getAttribute('data-link')),
          required: Boolean(step && step.hasAttribute('data-required')),
        };
        this.answers.length = this.current + 1;
        if (this.current + 1 < this.questions.length) this.goQuestion(this.current + 1, true);
        else this.finish();
        return;
      }
      if (t.closest('[data-finder-back]')) {
        if (this.current === 0) this.show(this.intro, true); else this.goQuestion(this.current - 1, true);
        return;
      }
      if (t.closest('[data-finder-restart]')) { this.answers = []; this.run += 1; this.goQuestion(0, true); }
    }

    show(step, focus) {
      if (!step) return;
      this.steps.forEach((s) => { s.hidden = s !== step; s.classList.toggle('is-active', s === step); });
      this.classList.toggle('is-results', step === this.result);
      if (!focus) return;
      const target = step.querySelector('[tabindex="-1"]') || step.querySelector('button, a');
      if (target) target.focus({ preventScroll: true });
      const top = this.getBoundingClientRect().top;
      if (top < 0) this.scrollIntoView({ block: 'start', behavior: S.reducedMotion ? 'auto' : 'smooth' });
    }

    goQuestion(i, focus) { this.current = i; this.show(this.questions[i], focus); }

    note(text, kind = 'none') {
      const p = document.createElement('p');
      p.className = `scentt-finder__note scentt-finder__note--${kind}`;
      p.textContent = String(text || '').slice(0, 300);
      this.box.append(p);
      return p;
    }

    grid() {
      let ul = this.box.querySelector('.scentt-finder__grid');
      if (!ul) {
        ul = document.createElement('ul');
        ul.className = 'scentt-finder__grid';
        ul.setAttribute('role', 'list');
        this.box.append(ul);
      }
      return ul;
    }

    /** Import a card from our own Section Rendering HTML. */
    addCard(ul, node, handle) {
      if (!node) return;
      const li = document.importNode(node, true);
      li.classList.add('scentt-finder__item');
      li.setAttribute('data-finder-h', handle);
      if (this.dataset.hover !== 'true') {
        li.querySelectorAll('.media--hover-effect').forEach((m) => {
          m.classList.remove('media--hover-effect');
          const imgs = m.querySelectorAll('img');
          if (imgs.length > 1) imgs[1].remove();
        });
      }
      ul.append(li);
    }

    setLink(href, label) {
      if (!this.link) return;
      this.link.hidden = false;
      this.link.setAttribute('href', href);
      this.link.textContent = label || this.linkLabel;
      this.link.removeAttribute('data-finder-expand');
      this.link.removeAttribute('aria-disabled');
      this.link.classList.remove('loading');
    }

    renderChips() {
      if (!this.chips) return;
      this.chips.replaceChildren(...this.answers.map((a) => {
        const s = document.createElement('span');
        s.className = 'scentt-finder__chip';
        s.textContent = a.label;
        return s;
      }));
    }

    async finish() {
      const run = (this.run += 1);
      this.lastMatches = null;
      const link = this.answers.reduce((l, a) => a.link || l, '');
      this.renderChips();
      this.box.replaceChildren();
      this.setLink(link || this.defaultHref);
      this.show(this.result, true);
      if (this.dataset.showProducts !== 'true' && link) return;
      this.box.classList.add('is-loading');
      this.box.setAttribute('aria-busy', 'true');
      let items;
      try {
        items = await loadIndex(this.allPath);
      } catch (e) {
        if (run === this.run) await this.fallbackSearch(run, link);
        return;
      }
      if (run !== this.run) return;
      const r = rank(items, this.answers);
      if (!link) {
        /* "See all my matches" lists every product that fits the answers, right here */
        const best = r.list.reduce((m, x) => Math.max(m, x.matched), 0);
        this.lastMatches = r.list.filter((m) => m.matched === best);
        if (this.link) { this.link.setAttribute('href', '#'); this.link.setAttribute('data-finder-expand', ''); }
      }
      if (this.dataset.showProducts !== 'true') { this.done(); return; }
      if (!r.list.length) {
        this.done();
        if (this.link && this.lastMatches) this.setLink(this.allPath);
        this.note(this.dataset.none);
        return;
      }
      const top = pickTop(r.list, this.count);
      const nodes = await Promise.all(top.map((m) => fetchCard(m.h)));
      if (run !== this.run) return;
      this.done();
      if (!nodes.some(Boolean)) {
        this.failed();
        return;
      }
      if (r.opt.length && top[0].matched < r.opt.length && this.dataset.relaxed) this.note(this.dataset.relaxed, 'relaxed');
      const ul = this.grid();
      nodes.forEach((n, k) => this.addCard(ul, n, top[k].h));
      this.updateMore();
    }

    done() {
      this.box.classList.remove('is-loading');
      this.box.removeAttribute('aria-busy');
    }

    failed() {
      const msg = this.dataset.error || S.config.strings.error;
      this.note(msg, 'error');
      S.toast(msg, 'error');
    }

    updateMore() {
      if (!this.link || !this.lastMatches || !this.link.hasAttribute('data-finder-expand')) return;
      const shown = new Set(Array.from(this.box.querySelectorAll('[data-finder-h]')).map((li) => li.getAttribute('data-finder-h')));
      const left = this.lastMatches.filter((m) => !shown.has(m.h)).length;
      this.link.hidden = left === 0;
    }

    async showMore(btn) {
      if (!this.lastMatches) return;
      const run = this.run;
      const ul = this.grid();
      const shown = new Set(Array.from(ul.querySelectorAll('[data-finder-h]')).map((li) => li.getAttribute('data-finder-h')));
      const next = this.lastMatches.filter((m) => !shown.has(m.h)).slice(0, 12);
      if (!next.length) { btn.hidden = true; return; }
      btn.classList.add('loading');
      btn.setAttribute('aria-disabled', 'true');
      const nodes = await Promise.all(next.map((m) => fetchCard(m.h)));
      if (run !== this.run) return;
      btn.classList.remove('loading');
      btn.removeAttribute('aria-disabled');
      if (!nodes.some(Boolean)) { this.failed(); return; }
      nodes.forEach((n, k) => this.addCard(ul, n, next[k].h));
      if (this.dataset.more) btn.textContent = this.dataset.more;
      this.updateMore();
    }

    searchUrl(words) {
      return `${this.searchPath}?type=product&options%5Bprefix%5D=none&q=${encodeURIComponent(words.join(' ').slice(0, 300))}`;
    }

    /* Index unavailable: ask Shopify search, keeping must-match answers and dropping the last optional ones first. */
    async fallbackSearch(run, link) {
      const req = [];
      const opt = [];
      this.answers.forEach((a) => { if (a.keyword) (a.required ? req : opt).push(a.keyword); });
      const tries = [];
      if (req.length || opt.length) for (let n = opt.length; n >= 0; n -= 1) tries.push(req.concat(opt.slice(0, n)));
      const urls = link ? [link] : tries.map((w) => this.searchUrl(w));
      if (this.link && !link) this.setLink(urls[0] || this.allPath);
      if (this.dataset.showProducts !== 'true' || !urls.length) { this.done(); return; }
      let networkErrors = 0;
      for (let i = 0; i < urls.length; i += 1) {
        let doc = null;
        try {
          doc = await S.fetchSection(urls[i], 'scentt-finder-results');
        } catch (e) {
          networkErrors += 1;
        }
        if (run !== this.run) return;
        const count = doc && doc.querySelector('[data-finder-count]');
        const items = doc ? Array.from(doc.querySelectorAll('.scentt-finder__item')) : [];
        if (count && S.int(count.getAttribute('data-finder-count'), 0, 0, 50) > 0 && items.length) {
          this.done();
          if (i > 0 && this.dataset.relaxed) this.note(this.dataset.relaxed, 'relaxed');
          const ul = this.grid();
          items.slice(0, this.count).forEach((li) => this.addCard(ul, li, li.getAttribute('data-handle') || ''));
          if (!link) this.setLink(urls[i]);
          return;
        }
      }
      this.done();
      if (networkErrors === urls.length) this.failed();
      else this.note(this.dataset.none);
    }
  }

  customElements.define('scentt-finder', ScenttFinder);
})();
